import { Card, Empty, Flex, Grid, Skeleton, Table } from "antd";
import { useEffect, useRef, useState } from "react";
import { border, radius, shadow } from "../../theme/tokens";

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 30, 50, 100];

// Số dự phòng cho lần render đầu (chưa có DOM để đo) và sàn chiều cao thân bảng.
const FALLBACK_HEAD_HEIGHT = 40;
const PAGINATION_MARGIN = 16; // antd đặt margin 16px trên/dưới cho .ant-table-pagination
const H_SCROLLBAR_HEIGHT = 8; // scrollbar ngang custom 6px + 2px đệm
// Dưới mức này thì thôi khoá chiều cao, trả về cuộn trang như cũ. 320px ~ 8 dòng
// (dòng size small ~39px). Lý do chọn 8: laptop 1366x768 chỉ còn ~5 dòng nếu khoá khung,
// tức tệ hơn cuộn trang (cuộn trang thì page header + toolbar trôi đi, thấy được ~15 dòng).
const MIN_BODY_HEIGHT = 320;

/**
 * Đo chiều cao còn lại của khung để gán cho `scroll.y` của bảng.
 *
 * Cách này thay cho `calc(100vh - <số cứng>)`: khung là flex item nên khi thanh công cụ
 * bung thêm hàng bộ lọc, chiều cao khung đổi → ResizeObserver bắn → đo lại. Không có
 * magic number nào phải chỉnh tay khi UI phía trên thay đổi.
 *
 * @param {boolean} enabled
 * @param {string} signature Chuỗi đại diện dữ liệu/cột, đổi thì đo lại (phân trang
 *                           chỉ xuất hiện khi có dữ liệu, không đo lại sẽ bị cắt cụt).
 */
function useFitBodyHeight(enabled, signature) {
    const frameRef = useRef(null);
    const [bodyHeight, setBodyHeight] = useState();

    useEffect(() => {
        const frame = frameRef.current;
        if (!enabled || !frame) {
            setBodyHeight(undefined);
            return;
        }

        const measure = () => {
            const available = frame.clientHeight;
            if (!available) return;

            const head =
                frame.querySelector(".ant-table-thead")?.offsetHeight || FALLBACK_HEAD_HEIGHT;
            const pager = frame.querySelector(".ant-table-pagination");
            const body = frame.querySelector(".ant-table-body");

            const chrome =
                head +
                (pager ? pager.offsetHeight + PAGINATION_MARGIN * 2 : 0) +
                (body && body.scrollWidth > body.clientWidth ? H_SCROLLBAR_HEIGHT : 0);

            // Khung còn quá thấp (viewport ngắn, hoặc màn có khối KPI phía trên) thì
            // bỏ khoá chiều cao, để khung tự cuộn — ép sàn sẽ cắt cụt phần phân trang.
            const next = Math.floor(available - chrome);
            const value = next >= MIN_BODY_HEIGHT ? next : undefined;
            setBodyHeight((prev) => (prev === value ? prev : value));
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(frame);
        return () => observer.disconnect();
    }, [enabled, signature]);

    return { frameRef, bodyHeight };
}

/**
 * Bảng danh sách dùng chung (chuẩn "Lịch sử gửi tin"):
 *  - Card bao ngoài + Table bordered, size small, scroll ngang.
 *  - Trạng thái loading hiển thị Skeleton, rỗng hiển thị Empty.
 *  - Pagination thống nhất (showTotal, pageSizeOptions...).
 *
 * Mọi prop khác của <Table> đều truyền thẳng qua `...rest`
 * (rowClassName, expandable, rowSelection, sticky...).
 *
 * @param {object[]} columns
 * @param {object[]} dataSource
 * @param {boolean}  loading
 * @param {object|false} pagination  Truyền false để ẩn phân trang.
 * @param {function} onChange
 * @param {string}   rowKey
 * @param {string}   emptyText
 * @param {boolean}  fitHeight  Bảng vừa khít khung màn hình: header + cột fixed đứng yên,
 *                              chỉ thân bảng cuộn, phân trang luôn nhìn thấy.
 *                              Yêu cầu component cha là flex column có chiều cao xác định
 *                              (dùng <BrandedPageLayout fitHeight>). Tự tắt trên mobile
 *                              để tránh cuộn lồng nhau.
 * @param {object}   scroll     Ghi đè cấu hình scroll nếu màn có nhu cầu riêng.
 */
export default function ListTableCard({
    columns,
    dataSource = [],
    loading = false,
    pagination,
    onChange,
    rowKey = "id",
    emptyText = "Không có dữ liệu",
    fitHeight = false,
    scroll,
    ...rest
}) {
    const screens = Grid.useBreakpoint();
    // Lần render đầu useBreakpoint trả về {} -> coi như desktop để không nhảy layout.
    const fitEnabled = fitHeight && screens.md !== false;

    const { frameRef, bodyHeight } = useFitBodyHeight(
        fitEnabled,
        `${dataSource.length}|${loading}|${columns?.length}|${pagination === false}`
    );

    const paginationConfig =
        pagination === false
            ? false
            : {
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
                showTotal: (total, range) => `Hiển thị từ ${range[0]} → ${range[1]} trong tổng ${total} giá trị`,
                ...pagination,
            };

    return (
        <Card
            size="small"
            style={{
                borderRadius: radius.md,
                border: border.hairline,
                boxShadow: shadow.xs,
                ...(fitEnabled
                    ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }
                    : null),
            }}
            styles={{
                body: {
                    padding: 0,
                    ...(fitEnabled
                        ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }
                        : null),
                },
            }}
        >
            <div
                ref={frameRef}
                style={fitEnabled ? { flex: 1, minHeight: 0, overflow: "auto" } : undefined}
            >
                <Table
                    columns={columns}
                    dataSource={loading ? [] : dataSource}
                    rowKey={rowKey}
                    loading={dataSource.length === 0 ? loading : false}
                    pagination={paginationConfig}
                    onChange={onChange}
                    locale={{
                        emptyText: loading ? (
                            <Flex vertical align="center" justify="center" gap={20} style={{ padding: "30px 0" }}>
                                <Skeleton.Input active size="large" style={{ width: 250 }} />
                                <Skeleton.Input active size="small" style={{ width: 150 }} />
                            </Flex>
                        ) : (
                            <Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        ),
                    }}
                    bordered
                    size="small"
                    scroll={{
                        x: "max-content",
                        ...(bodyHeight ? { y: bodyHeight } : null),
                        ...scroll,
                    }}
                    {...rest}
                />
            </div>
        </Card>
    );
}
